import { z } from 'zod';
import { AgentTierSchema } from './agent.js';
import { RecruiterTierSchema } from './recruiter.js';

/**
 * `platformConfig/commissionRules` — super-admin-editable rules that
 * drive both the existing agent commission engine and (Sprint 80+) the
 * new recruiter splits. Stored as a singleton doc per platform brand
 * (or platform-wide if the brand doesn't override).
 *
 * Before Sprint 80 the rates lived as an inline interface inside the
 * commission helper; codifying as a Zod schema unblocks the super-admin
 * editor + makes per-brand overrides addressable.
 */

const RateSchema = z.number().min(0).max(1).describe('0..1; e.g., 0.20 = 20%');
const MoneySenSchema = z.number().int().nonnegative();

/**
 * Per-tier multiplier on the agent's pre-multiplier commission
 * subtotal. Field name kept as `tierMultipliers` (not
 * `agentTierMultipliers`) to align with existing Firestore data
 * written before Sprint 80.
 */
export const TierMultipliersSchema = z
  .record(AgentTierSchema, z.number().positive())
  .describe("Per-tier multiplier on the agent's pre-multiplier commission subtotal");
export type TierMultipliers = z.infer<typeof TierMultipliersSchema>;

export const RecruiterTierMultipliersSchema = z
  .record(RecruiterTierSchema, z.number().positive())
  .describe("Per-tier multiplier on the recruiter's pre-multiplier subtotal");
export type RecruiterTierMultipliers = z.infer<typeof RecruiterTierMultipliersSchema>;

/**
 * First-invoice fees split. Both signup + activation fees on the
 * operator's first invoice generate accruals to:
 *   - The agent that signed up the operator (per existing flow)
 *   - The recruiter that signed up THAT agent (Sprint 80+)
 *
 * All rates default to 0 so the existing platform behaves exactly as
 * before until super-admin opts into the new revenue layer.
 */
export const FirstInvoiceSplitsSchema = z.object({
  /** Share of signup fee that flows to the agent. */
  signupAgentSharePct: RateSchema.default(0),
  /** Share of signup fee that flows to the recruiter (when present). */
  signupRecruiterSharePct: RateSchema.default(0),
  /** Share of activation fee that flows to the agent. */
  activationAgentSharePct: RateSchema.default(0),
  /** Share of activation fee that flows to the recruiter (when present). */
  activationRecruiterSharePct: RateSchema.default(0),
});
export type FirstInvoiceSplits = z.infer<typeof FirstInvoiceSplitsSchema>;

export const CommissionRulesSchema = z.object({
  /**
   * Sprint 95 — agent commission is one-time, equal to the operator's
   * first month subscription line sum × this multiplier × tier. Default
   * 1.0 means "agent earns one month of the operator's subscription".
   * Set 0.5 for half a month, 2.0 for two months, etc.
   */
  signupCommissionMultiplier: z
    .number()
    .min(0)
    .max(10)
    .default(1.0)
    .describe('Multiplier on first-month subscription sen → agent one-time commission'),
  /**
   * Sprint 95 — recruiter commission is a flat sen amount paid ONCE per
   * downstream operator signup (when one of the recruiter's agents
   * signs up an operator). Multiplied by recruiter tier.
   */
  recruiterPerSignupSen: MoneySenSchema.default(0).describe(
    'Flat sen paid to recruiter per downstream operator signup, before tier multiplier',
  ),
  /**
   * ADR-0029 — Survival Bonus. A SECOND one-time payment that fires when a
   * signed operator is still paying `survivalBonusThresholdDays` after their
   * first paid invoice. Re-points the agent from raw signup volume toward
   * signing operators who *stay* (Kilat's only compounding revenue term)
   * without reintroducing recurring commission.
   *
   * Agent bonus = floor(subscription line sum of the qualifying invoice
   *   × survivalBonusMultiplier × agent tier multiplier).
   * Default 0 → the whole feature is a no-op until super-admin sets it,
   * matching how every new commission layer ships (firstInvoice, signupBonus).
   */
  survivalBonusMultiplier: z
    .number()
    .min(0)
    .max(10)
    .default(0)
    .describe(
      "Multiplier on the operator's monthly subscription sen → agent one-time survival bonus. 0 = off",
    ),
  /**
   * Days after the operator's first paid invoice at which "still paying"
   * qualifies for the survival bonus. A later paid invoice at/after this many
   * days IS the proof of survival, so this is event-driven, not swept.
   */
  survivalBonusThresholdDays: z
    .number()
    .int()
    .min(1)
    .max(365)
    .default(90)
    .describe('Days after first paid invoice at which continued payment earns the survival bonus'),
  /**
   * Flat sen paid ONCE to the recruiter (if any) when a downstream operator
   * survives to the threshold, before recruiter tier multiplier. 0 = off.
   */
  recruiterSurvivalBonusSen: MoneySenSchema.default(0).describe(
    'Flat sen paid to recruiter when a downstream operator survives to the threshold, before tier multiplier',
  ),
  /**
   * RESIDUAL (trailing) commission — the agent's share of EVERY paid invoice
   * after the operator's first, for as long as the operator keeps paying and
   * the agent stays active. Un-deprecated by ADR-0034: Sprint 95 retired the
   * accrual that consumed this, but the field, its maths and its tests all
   * survived, and retiring it left the channel with nothing that retains the
   * AGENT (the survival bonus retains the operator).
   *
   * 0 = off, which is the default — nobody starts earning because a deploy
   * happened (ADR-0033). Set from Super-admin -> Plans -> Commission rules.
   * NOTE the ratchet: a residual is a promise to a partner who builds a book
   * on it. Raising it is routine; cutting it is the single most damaging move
   * available to a sales channel. Start low.
   */
  recurringRate: RateSchema.default(0).describe(
    'Agent share of each subsequent paid invoice (0 = residual off)',
  ),
  /**
   * Residual rate applied to extra-outlet and mid-month proration lines, so an
   * agent whose operator GROWS earns more. Same ratchet warning as above.
   */
  perExtraOutletRate: RateSchema.default(0).describe(
    'Agent share of extra-outlet + proration lines on subsequent invoices',
  ),
  /** Flat sen bonus on operator's first paid invoice. Off by default. */
  signupBonus: MoneySenSchema.default(0).describe('Flat sen bonus to agent on first paid invoice'),
  /** Per-tier multiplier on the agent's pre-multiplier subtotal. */
  tierMultipliers: TierMultipliersSchema.default({
    bronze: 1,
    silver: 1.1,
    gold: 1.25,
  }),
  /** Per-tier multiplier on the recruiter's pre-multiplier subtotal. */
  recruiterTierMultipliers: RecruiterTierMultipliersSchema.default({
    bronze: 1,
    silver: 1.1,
    gold: 1.25,
  }),
  /**
   * RESIDUAL (trailing) commission, recruiter side — a share of every paid
   * invoice after the first from operators signed by agents this recruiter
   * brought in. Un-deprecated by ADR-0034 alongside the agent residual; same
   * ratchet warning (raise freely, cutting breaks the channel).
   *
   * Applies to subscription lines only — recruiters are paid on agent
   * throughput, not on deal size, which is why the flat per-signup fee exists
   * separately. 0 = off, the default.
   */
  recruiterRecurringRate: RateSchema.default(0).describe(
    'Recruiter share of each subsequent paid invoice downstream (0 = off)',
  ),
  /** First-invoice fee splits — the on-boarding revenue layer. */
  firstInvoice: FirstInvoiceSplitsSchema.default({
    signupAgentSharePct: 0,
    signupRecruiterSharePct: 0,
    activationAgentSharePct: 0,
    activationRecruiterSharePct: 0,
  }),
  /** Auto-promotion thresholds (active operators required to advance tier). */
  autoPromotionThresholds: z
    .object({
      silverActiveOperators: z.number().int().positive().default(5),
      goldActiveOperators: z.number().int().positive().default(15),
    })
    .default({ silverActiveOperators: 5, goldActiveOperators: 15 }),
  /** Days after a refund within which the original accrual is reversed. */
  clawbackWindowDays: z.number().int().nonnegative().default(30),
  /** Minimum sen accrual required before a payout is initiated. */
  payoutThreshold: MoneySenSchema.default(10_000),
  /** Method super-admin uses to pay accumulated accruals. */
  payoutMethod: z.enum(['duitnow', 'bank_transfer']).default('duitnow'),
  /** Day of the month the payout job runs. */
  payoutDayOfMonth: z.number().int().min(1).max(28).default(7),
});
export type CommissionRules = z.infer<typeof CommissionRulesSchema>;
